import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Icon, Placeholder } from 'semantic-ui-react';
import { LoginPopupsService } from '~/api/services/loginPopups';
import cloneDeep from 'lodash.clonedeep'
import { imageTypes, pdfType, videoTypes } from '~/Constants';
import Word from '@assets/images/Word.png';
import Excel from '@assets/images/Excel.png';
import Documents from '@assets/images/Documents.png';
import {
  InformationWrapper,
  StyledButtonLeft,
  StyledButtonRight,
  StyledCard,
  StyledCarousel,
  StyledCarouselItem,
  StyledCarouselSlide,
  TitleWrapper
}
  from './style';
import { ThemeContext } from 'styled-components';
import { DownloadService } from '~/api/services/downloadFiles';
import { handleErrors } from '~/utils/helpers';

type CarouselProps = {
  items: any[]
  headerRef?: React.RefObject<HTMLHeadingElement>
};

// TO-DO
// make this component more dynamic
// all api must be handled outside of the component

const Carousel: React.FC<CarouselProps> = (props) => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(-1);
  const [items, setItems] = useState([])
  const [loadingItem, setLoadingItem] = useState(false)
  const { firstColor } = useContext(ThemeContext)
  const refIframe = useRef(null)

  useEffect(() => {
    setActiveIndex(0)
  }, [props.items])

  useEffect(() => {
    if (props.headerRef?.current && activeIndex >= 0) {
      const itemsData = items.length > 0 ? items : props.items
      props.headerRef.current.innerText = itemsData[activeIndex]?.title || ''
    }
    if (activeIndex >= 0) {
      const itemsData = items.length > 0 ? items : props.items
      if (itemsData[activeIndex] && itemsData[activeIndex].content && !itemsData[activeIndex].src) {
        fetchLoginPopupUrl()
      } else if (itemsData[activeIndex] && !itemsData[activeIndex].content) {
        setItems(itemsData)
      }
    }
  }, [activeIndex, props.headerRef])

  function getType(type: string) {
    if (imageTypes.find(typeI => typeI === type)) {
      return `image/${type}`
    } else if (videoTypes.find(typeI => typeI === type)) {
      return `video/${type}`
    } else if (pdfType === type) {
      return `application/${type}`
    } else if (type === 'xlxs' || type === 'docx') {
      return `application/${type}`
    }
    return type
  }

  async function fetchLoginPopupUrl() {
    setLoadingItem(true)
    try {
      const itemsData = items.length > 0 ? items : props.items
      const response = await LoginPopupsService.getFarmerLoginPopupUrl(itemsData[activeIndex].id)
      const itemsTemp = cloneDeep(itemsData)
      itemsTemp[activeIndex].src = response.data
      itemsTemp[activeIndex].type = getType(itemsData[activeIndex].content.split('.')[1])
      setItems(itemsTemp)
      setLoadingItem(false)
    } catch (error) {
      setLoadingItem(false)
      handleErrors(error)
    }
  }

  const handlePrevClick = () => {
    setActiveIndex((prevIndex) => (prevIndex === 0 ? 0 : prevIndex - 1));
  };

  const handleNextClick = () => {
    setActiveIndex((prevIndex) => (prevIndex === 0 ? 1 : prevIndex + 1));
  };

  function renderPlaceholder() {
    return <Placeholder>
      <Placeholder.Image square />
    </Placeholder>
  }

  function handleRefreshClick() {
    {
      if (refIframe.current) {
        const file = items[activeIndex]
        refIframe.current.setAttribute('src', '')
        const encodedUrl = encodeURIComponent(file.src);
        // Construct the embedded URL
        const embeddedUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
        refIframe.current.setAttribute('src', embeddedUrl)
      }
    }
  }

  function renderApplicationDocuments() {
    const file = items[activeIndex]
    if (file?.type?.startsWith('application/pdf')) {
      // Encode the PDF URL
      const encodedUrl = encodeURIComponent(file.src);

      // Construct the embedded URL
      const embeddedUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true#rm=minimal`;

      return <iframe ref={refIframe} style={{ width: '100%', height: '100%' }} src={embeddedUrl} />;
    }
    else if (file?.name.includes('.docx')) {
      return <img src={Word} alt='Preview' />
    }
    else if (file?.name.includes('.xlsx')) {
      return <img src={Excel} alt='Preview' />
    }

    return <img src={Documents} alt='Preview' />
  }

  function renderItem() {
    const file = items[activeIndex]
    if (file?.type) {
      const basicType = file?.type?.split('/')[0]
      switch (basicType) {
        case 'image':
          return <img src={file.src as string} alt='Preview' />
        case 'video':
          return <video src={file.src as string} controls />
        case 'application':
          return renderApplicationDocuments()
        default:
          return <img src={Documents} alt='Preview' />
      }
    } else {
      return renderPlaceholder()
    }
  }

  function handleContextMenu(event) {
    if (!items[activeIndex]?.downloadEnabled) {
      event.preventDefault();
    }
  }

  async function handleDownloadItem() {
    const file = items[activeIndex]
    try {
      await DownloadService.downloadFileFromAws(file?.src, file?.name)
    } catch (error) {
      handleErrors(error)
    }
  }

  function renderCarouselItem() {
    return <div style={{ height: '100%' }} onContextMenu={handleContextMenu}>{renderItem()}</div>
  }

  const file = items[activeIndex]
  const basicType = file?.type?.split('/')[0]

  function renderContentFile() {
    return <StyledCard fluid>
      <TitleWrapper>
        {activeIndex >= 0 && Boolean(items[activeIndex]?.downloadEnabled) && file?.src && <Button type={'button'}
          onClick={() => handleDownloadItem()}
          basic
          pdfAlignRight={items[activeIndex]?.type?.startsWith('application/pdf')}
          icon={'cloud download'}
          content={t('Download')} />}
        {activeIndex >= 0 && items[activeIndex]?.type?.startsWith('application/pdf') && <Button type={'button'}
          onClick={() => handleRefreshClick()}
          basic
          icon={'refresh'}
          content={t('Refresh')} />
        }
      </TitleWrapper>
      {items[activeIndex]?.headerText && <InformationWrapper>
        <div className='left' dangerouslySetInnerHTML={{ __html: items[activeIndex]?.headerText }} />
      </InformationWrapper>}
      <Card.Content>
        <StyledCarousel $videoType={basicType === 'video'}>
          <StyledCarouselSlide $pdfType={basicType === 'application'}>
            <StyledCarouselItem $pdfType={basicType === 'application'} $width={basicType === 'video' ? '75%' : '50%'}>
              {loadingItem || activeIndex < 0 ? renderPlaceholder() : renderCarouselItem()}
            </StyledCarouselItem>
          </StyledCarouselSlide>
        </StyledCarousel>
        {items[activeIndex]?.footerText && <InformationWrapper>
          <div dangerouslySetInnerHTML={{ __html: items[activeIndex]?.footerText }} />
        </InformationWrapper>}
      </Card.Content>
    </StyledCard>
  }

  function renderTextOnly() {
    return <div>
      <div dangerouslySetInnerHTML={{ __html: items[activeIndex]?.headerText }} />
      {items[activeIndex]?.footerText && <div dangerouslySetInnerHTML={{ __html: items[activeIndex]?.footerText }} />}
    </div>
  }

  const textOnlyFile = !file?.content && file?.headerText

  return <div style={{ position: 'relative', width: '100%' }}>
    {textOnlyFile ? renderTextOnly() : renderContentFile()}
    {items.length > 1 && <StyledButtonLeft
      $textOnlyFile={textOnlyFile}
      disabled={activeIndex === 0}
      icon
      onClick={handlePrevClick}
    >
      <Icon name='arrow alternate circle left' style={{ color: firstColor }} />
    </StyledButtonLeft>
    }
    {items.length > 1 && <StyledButtonRight
      disabled={activeIndex === items.length - 1}
      icon
      $textOnlyFile={textOnlyFile}
      onClick={handleNextClick}
    >
      <Icon name='arrow alternate circle right' style={{ color: firstColor }} />
    </StyledButtonRight>
    }
  </div>
};

export default Carousel;

